import React, { useCallback, useMemo, useState } from 'react';
import { ArcStack, ArcText } from '@src/core/ui';
import type { BarChartType } from '../../components/BarChart/types';
import { AnalyticsChartSection } from '../../components/AnalyticsChartSection';
import { SummaryStatCard } from '../../components/SummaryStatCard';
import { getChartIntervalApiParams } from '../../components/ChartTypeSwitcher';
import { useChartData } from '../../hooks';
import { useGetAnalyticsChartsQuery, useGetAnalyticsMetricsQuery } from '../../redux/api';
import { CHART_TYPE_TO_AGGREGATION } from '../../constants';
import { formatFlightTime, formatFlightDistance, formatDataUsage, formatCount } from '../../utils';

const SUMMARY_LABELS = [
  'Active Drones',
  'Total Flight Count',
  'Total Flight Time',
  'Total Flight Distance',
  'Total Data Usage',
] as const;

export const AnalyticsPage: React.FC = () => {
  const [flightCountChartType, setFlightCountChartType] = useState<BarChartType>('daily');
  const [dataUsageChartType, setDataUsageChartType] = useState<BarChartType>('daily');
  const [flightCountPeriodOffset, setFlightCountPeriodOffset] = useState(0);
  const [dataUsagePeriodOffset, setDataUsagePeriodOffset] = useState(0);

  const onFlightCountChartTypeChange = useCallback((value: BarChartType) => {
    setFlightCountChartType(value);
    setFlightCountPeriodOffset(0);
  }, []);
  const onDataUsageChartTypeChange = useCallback((value: BarChartType) => {
    setDataUsageChartType(value);
    setDataUsagePeriodOffset(0);
  }, []);

  const { data: analyticsMetrics } = useGetAnalyticsMetricsQuery();

  const flightCountParams = useMemo(
    () => ({
      aggregation: CHART_TYPE_TO_AGGREGATION[flightCountChartType],
      ...getChartIntervalApiParams(flightCountChartType, flightCountPeriodOffset),
    }),
    [flightCountChartType, flightCountPeriodOffset]
  );
  const dataUsageParams = useMemo(
    () => ({
      aggregation: CHART_TYPE_TO_AGGREGATION[dataUsageChartType],
      ...getChartIntervalApiParams(dataUsageChartType, dataUsagePeriodOffset),
    }),
    [dataUsageChartType, dataUsagePeriodOffset]
  );

  const { data: flightCountCharts } = useGetAnalyticsChartsQuery(flightCountParams);
  const { data: dataUsageCharts } = useGetAnalyticsChartsQuery(dataUsageParams);

  const summaryStats = useMemo(() => {
    if (!analyticsMetrics) {
      return SUMMARY_LABELS.map((label) => ({ label, value: '—' as const }));
    }
    return [
      { label: SUMMARY_LABELS[0], value: formatCount(analyticsMetrics.activeDrones) },
      { label: SUMMARY_LABELS[1], value: formatCount(analyticsMetrics.totalFlightCount) },
      { label: SUMMARY_LABELS[2], value: formatFlightTime(analyticsMetrics.totalFlightTime) },
      { label: SUMMARY_LABELS[3], value: formatFlightDistance(analyticsMetrics.totalFlightDistance) },
      { label: SUMMARY_LABELS[4], value: formatDataUsage(analyticsMetrics.totalDataUsage) },
    ];
  }, [analyticsMetrics]);

  const flightCountData = useChartData(
    flightCountCharts?.flightCount,
    flightCountChartType,
    flightCountPeriodOffset
  );
  const dataUsageData = useChartData(
    dataUsageCharts?.dataUsage,
    dataUsageChartType,
    dataUsagePeriodOffset
  );

  return (
    <ArcStack p={8} pt={6}>
      <ArcText.h3 fontWeight={700}>Drone Analytics</ArcText.h3>
      <ArcText.caption fontSize={16}>Key metrics across your drone fleet</ArcText.caption>

      <ArcStack marginTop={3} direction="row" gap={2}>
        {summaryStats.map(({ label, value }) => (
          <SummaryStatCard key={label} label={label} value={value} />
        ))}
      </ArcStack>

      <ArcStack marginTop={3.5} spacing={3.5}>
        <AnalyticsChartSection
          title="Total Flight Count"
          chartType={flightCountChartType}
          periodOffset={flightCountPeriodOffset}
          onChartTypeChange={onFlightCountChartTypeChange}
          onPeriodOffsetChange={setFlightCountPeriodOffset}
          data={flightCountData}
        />
        <AnalyticsChartSection
          title="Total Data Usage"
          chartType={dataUsageChartType}
          periodOffset={dataUsagePeriodOffset}
          onChartTypeChange={onDataUsageChartTypeChange}
          onPeriodOffsetChange={setDataUsagePeriodOffset}
          data={dataUsageData}
          valueSuffix=" GB"
        />
      </ArcStack>
    </ArcStack>
  );
};
